import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import { isEmpty, isValidEmail } from '../helpers/validator';
import webServices from '../services/webServices';
import GiftLandingComponent from '../components/home/gift/GiftLandingComponent';
import PersonDetailsComponent from '../components/home/gift/PersonDetailsComponent';
import ReferrerDetailsComponent from '../components/home/gift/ReferrerDetailsComponent';
import MatchmakerSuccessComponent from '../components/home/gift/MatchmakerSuccessComponent';
import Spinner from '../components/general/spinner';
import HomeHeaderMobile from '../layouts/partial/homeHeaderMobile';
import recommendPhoto from '../assets/images/matchmaker-recommend.webp';
import detailsPhoto from '../assets/images/matchmaker-details.webp';
import '../assets/scss/matchmaker.scss';

/**
 * "Be a Matchmaker" flow (free — no plan / payment step).
 *   1  Landing            (GiftLandingComponent)
 *   2  Recommend someone  (PersonDetailsComponent)   -> firstName/lastName/email/relationship
 *   3  Your details       (ReferrerDetailsComponent) -> pFirstName/pLastName/pEmail, submits
 *   4  Success            (MatchmakerSuccessComponent)
 */
const INITIAL_VALUES = {
  firstName: '',
  lastName: '',
  email: '',
  relationship: '',
  pFirstName: '',
  pLastName: '',
  pEmail: ''
};

const GiftPageComponent = () => {
  const [step, setStep] = useState(1);
  const [isSpinner, setIsSpinner] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formValidation, setFormValidation] = useState({ ...INITIAL_VALUES });
  const [values, setValues] = useState({ ...INITIAL_VALUES });

  // Steps 2-4 set their photo as a CSS background, which the browser only
  // fetches once that step renders — on a slow phone connection the step
  // would appear without its photo for a moment. Warm the cache up front.
  useEffect(() => {
    [recommendPhoto, detailsPhoto].forEach((src) => {
      new Image().src = src;
    });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsSpinner(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const handleValidationPersonal = () => {
    let isError = false;
    const formerr = { ...formValidation };

    if (isEmpty(values.firstName)) {
      isError = true;
      formerr.firstName = 'The first name field is required.';
    }
    if (isEmpty(values.lastName)) {
      isError = true;
      formerr.lastName = 'The last name field is required.';
    }
    if (isEmpty(values.email)) {
      isError = true;
      formerr.email = 'The email field is required.';
    } else if (!isValidEmail(values.email)) {
      isError = true;
      formerr.email = 'Please enter a valid email.';
    }
    if (isEmpty(values.relationship)) {
      isError = true;
      formerr.relationship = 'Please select your relationship to this person.';
    }

    setFormValidation(formerr);
    return isError;
  };

  const handleValidationPDetails = () => {
    let isError = false;
    const formerr = { ...formValidation };

    if (isEmpty(values.pFirstName)) {
      isError = true;
      formerr.pFirstName = 'The first name field is required.';
    }
    if (isEmpty(values.pLastName)) {
      isError = true;
      formerr.pLastName = 'The last name field is required.';
    }
    if (isEmpty(values.pEmail)) {
      isError = true;
      formerr.pEmail = 'The email field is required.';
    } else if (!isValidEmail(values.pEmail)) {
      isError = true;
      formerr.pEmail = 'Please enter a valid email.';
    } else if (
      values.email &&
      values.pEmail.trim().toLowerCase() === values.email.trim().toLowerCase()
    ) {
      isError = true;
      formerr.pEmail = 'Your email must be different from the person you are recommending.';
    }

    setFormValidation(formerr);
    return isError;
  };

  const handleChangeValue = (props) => (e) => {
    setFormValidation({ ...formValidation, [props]: '' });
    setValues({ ...values, [props]: e.target.value });
  };

  const handleSelectValue = (props, value) => {
    setFormValidation({ ...formValidation, [props]: '' });
    setValues({ ...values, [props]: value });
  };

  const submitRecommendation = async () => {
    setIsSubmitting(true);
    try {
      const response = await webServices.createOrderForGift({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        relationship: values.relationship,
        pFirstName: values.pFirstName.trim(),
        pLastName: values.pLastName.trim(),
        pEmail: values.pEmail.trim()
      });
      if (response && response.success) {
        setStep(4);
        return;
      }
      // postRequest resolves with the axios error rather than rejecting, and the
      // response interceptor has already shown a readable message for it —
      // toasting response.message here would repeat it as raw "Network Error".
      if (response instanceof Error) return;
      toast.error(
        (response && response.message) ||
          'We could not send your recommendation. Please try again.'
      );
    } catch {
      toast.error('We could not send your recommendation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToNextStep = (nextStep) => {
    if (nextStep === 3 && handleValidationPersonal()) return false;
    if (nextStep === 4) {
      if (handleValidationPDetails()) return false;
      submitRecommendation();
      return null;
    }
    setStep(nextStep);
    return null;
  };

  const recommendAnother = () => {
    setValues({ ...INITIAL_VALUES });
    setFormValidation({ ...INITIAL_VALUES });
    setStep(2);
  };

  const RenderComponent = () => {
    if (step === 2) {
      return (
        <PersonDetailsComponent
          goToNextStep={goToNextStep}
          handleChange={handleChangeValue}
          handleSelect={handleSelectValue}
          formValidation={formValidation}
          values={values}
        />
      );
    }
    if (step === 3) {
      return (
        <ReferrerDetailsComponent
          goToNextStep={goToNextStep}
          handleChange={handleChangeValue}
          formValidation={formValidation}
          values={values}
          isSubmitting={isSubmitting}
        />
      );
    }
    if (step === 4) {
      return (
        <MatchmakerSuccessComponent
          values={values}
          onRecommendAnother={recommendAnother}
        />
      );
    }
    return <GiftLandingComponent goToNextStep={goToNextStep} />;
  };

  return (
    <>
      <Helmet>
        <title>Be a Matchmaker | Lovrican</title>
        <meta
          name="description"
          content="Recommend a friend or relative to Lovrican and kickstart their love journey. Become a matchmaker today — it’s free."
        />
        <meta name="keywords" content="Become a Matchmaker" />
      </Helmet>
      {isSpinner ? (
        <Spinner active={isSpinner} />
      ) : (
        <>
          {/* Releases sheet, Development r6/r23: the matchmaker process needs
              the burger / side menu. The header is position: fixed and holds
              the logo too, hence mounted once here rather than per step. */}
          <HomeHeaderMobile pinned />
          {RenderComponent()}
        </>
      )}
    </>
  );
};

export default GiftPageComponent;
